import React, { useState, useEffect } from "react";
import "./Style.css";
import { ArrowUp, ArrowUpRight, ChevronDown, List } from "react-bootstrap-icons";
import BookDemoModal from "./BookDemoModal";
import Footer from "./Footer";
import CompanyLoginModal from "./CompanyLoginModal";

const navSolutions = [
  { icon: "ph-users-three", title: "HR Operations", desc: "Manage employee lifecycle, org structure and all HR workflows in one place." },
  { icon: "ph-money-wavy", title: "Payroll & Finance", desc: "Automate salary processing, payslip generation and tax deductions." },
  { icon: "ph-shield-check", title: "Tax & Compliance", desc: "PF, PT, TDS and GST filings handled end-to-end without manual effort." },
  { icon: "ph-user-plus", title: "Talent Acquisition", desc: "Track candidates, issue offer letters and onboard new hires seamlessly." },
  { icon: "ph-receipt", title: "Invoice & Billing", desc: "Create professional invoices, quotations and manage client accounts." },
  { icon: "ph-fingerprint", title: "Attendance & Leaves", desc: "Monitor daily and monthly attendance with streamlined leave workflows." },
  { icon: "ph-files", title: "Document Generation", desc: "Experience letters, certificates and appraisal documents in seconds." },
  { icon: "ph-user-gear", title: "Employee Self-Service", desc: "Employees access payslips, apply leaves and update their own data." },
];

const whyCubPoints = [
  { icon: "ph-squares-four", title: "All-in-One Platform", desc: "HR, Payroll, Tax, Invoicing — one login, zero tool switching." },
  { icon: "ph-lightning", title: "Automation First", desc: "From salary calc to PDF letters, we automate the heavy lifting." },
  { icon: "ph-lock-key", title: "Secure by Design", desc: "Role-based access with full company-level data isolation." },
  { icon: "ph-trend-up", title: "Built to Scale", desc: "Grows from 10 to 10,000 employees without a single friction point." },
  { icon: "ph-file-arrow-down", title: "Instant Documents", desc: "Generate offer letters, payslips and certificates in one click." },
  { icon: "ph-check-circle", title: "Compliance Ready", desc: "PF, PT, TDS and GST workflows baked directly into the platform." },
  { icon: "ph-chats-circle", title: "Employee Experience", desc: "A self-service portal that cuts HR overhead and empowers your team." },
  { icon: "ph-rocket-launch", title: "Quick to Go Live", desc: "Get your entire HR operation running in days, not months." },
];

const plans = [
  {
    tag: "Most Popular",
    name: "Business",
    desc: "The complete HR suite for small and growing businesses across India.",
    features: [
      "Up to 500 employees",
      "Employee Registration & Profiles",
      "Automated Payroll & Payslips",
      "Attendance & Leave Management",
      "Tax Compliance — PF · PT · TDS · GST",
      "Invoice & Client Management",
      "Candidate Onboarding",
      "Employee Self-Service (ESS) Portal",
      "Document Generation",
      "Email & Chat Support",
    ],
    cta: "Book a Demo",
    featured: true,
    enterprise: false,
  },
  {
    tag: "Custom Solution",
    name: "Enterprise",
    desc: "Unlimited scale, dedicated support, and custom integrations for large organisations.",
    features: [
      "Unlimited employees",
      "Everything in Business",
      "Multi-branch Management",
      "Custom Document Templates",
      "API Access & Integrations",
      "Dedicated Account Manager",
      "SLA-backed Support",
      "Custom Reporting & Analytics",
      "Priority Onboarding Assistance",
      "On-site Training (on request)",
    ],
    cta: "Book a Demo",
    featured: false,
    enterprise: true,
  },
];

const PricingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSolutionsOpen, setIsMobileSolutionsOpen] = useState(false);
  const [isMobileWhyCubOpen, setIsMobileWhyCubOpen] = useState(false);

  const handleScrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleCTA = () => setShowDemoModal(true);

  return (
    <div className="pricing-page-root">

      {/* ── Header (matches LandingPage) ── */}
      <header>
        <div className={`top-0 left-0 right-0 z-50 py-3 header headerAbsolute pricing-page-header${isScrolled ? " header-scrolled" : ""}`}>
          <div className="flex justify-between items-center container text-s1">

            <div className="flex justify-start items-center gap-3">
              <button
                className="lg:hidden text-3xl mobileMenuOpenButton"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <List className="text-3xl" />
              </button>
              <a href="/">
                <img src="assets/img/website/newDesCubLogo.png" alt="CUB HRM" className="h-[60px]" />
              </a>
            </div>

            {/* Desktop Nav */}
            <nav className="max-lg:hidden">
              <ul className="flex justify-center items-center gap-3">
                <li>
                  <a href="/" className="menu hover:header_menu_shadow duration-700 px-2 py-3 rounded-lg text-md">
                    Home
                  </a>
                </li>
                <li>
                  <a href="/#about" className="hover:header_menu_shadow duration-700 px-2 py-3 rounded-lg menu text-md">
                    About
                  </a>
                </li>

                {/* Solutions Mega Dropdown */}
                <li className="mega-nav-item">
                  <button className="mega-nav-trigger menu hover:header_menu_shadow duration-700 px-2 py-3 rounded-lg text-md flex items-center gap-1">
                    Solutions <ChevronDown className="mega-nav-chevron" />
                  </button>
                  <div className="mega-dropdown solutions-dropdown">
                    <div className="mega-left">
                      <p className="mega-left-eyebrow">What We Offer</p>
                      <h3 className="mega-left-heading">Built for Modern HR Teams</h3>
                      <p className="mega-left-sub">One platform covering your entire workforce lifecycle — HR, Payroll, Tax and more.</p>
                      <div className="mega-left-tags">
                        <span className="mega-tag">Workforce</span>
                        <span className="mega-tag">Finance</span>
                        <span className="mega-tag">Compliance</span>
                      </div>
                    </div>
                    <div className="mega-right">
                      <p className="mega-right-heading">Solutions for every team</p>
                      <div className="mega-grid">
                        {navSolutions.map((s, i) => (
                          <div key={i} className="mega-card">
                            <i className={`ph-fill ${s.icon} mega-card-icon`} />
                            <div>
                              <p className="mega-card-title">{s.title}</p>
                              <p className="mega-card-desc">{s.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>

                {/* Why CUB Mega Dropdown */}
                <li className="mega-nav-item">
                  <button className="mega-nav-trigger menu hover:header_menu_shadow duration-700 px-2 py-3 rounded-lg text-md flex items-center gap-1">
                    Why CUB <ChevronDown className="mega-nav-chevron" />
                  </button>
                  <div className="mega-dropdown whycub-dropdown">
                    <div className="mega-left whycub-left">
                      <p className="mega-left-eyebrow">Our Advantage</p>
                      <h3 className="mega-left-heading">The Smarter Way to Run HR</h3>
                      <p className="mega-left-sub">Trusted by growing companies to automate, secure and simplify HR operations.</p>
                      <div className="mega-left-stats">
                        <div className="mega-stat">
                          <span className="mega-stat-num">10x</span>
                          <span className="mega-stat-label">Faster Onboarding</span>
                        </div>
                      </div>
                    </div>
                    <div className="mega-right">
                      <p className="mega-right-heading">Reasons to choose CUB</p>
                      <div className="mega-grid">
                        {whyCubPoints.map((w, i) => (
                          <div key={i} className="mega-card">
                            <i className={`ph-fill ${w.icon} mega-card-icon whycub-icon`} />
                            <div>
                              <p className="mega-card-title">{w.title}</p>
                              <p className="mega-card-desc">{w.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>

                <li>
                  <a href="/pricing" className="hover:header_menu_shadow duration-700 px-2 py-3 rounded-lg menu text-md" style={{ color: "#1595cc", fontWeight: 600 }}>
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#pp-footer" className="hover:header_menu_shadow duration-700 px-2 py-3 rounded-lg menu text-md">
                    Contact Us
                  </a>
                </li>
              </ul>
            </nav>

            {/* Right side: Login + Sign Up */}
            <div className="flex items-center gap-3 max-sm:hidden">
              <a
                href=" "
                onClick={(e) => {
                  e.preventDefault();
                  setShowLoginModal(true);
                }}
                className="flex justify-center items-center gap-2 py-2 px-4 rounded-full border border-gray-300 text-s1 font-medium text-md hover:border-s1 hover:text-s1 transition-colors duration-200"
              >
                Log In
              </a>
              <a
                href="/anonymouseCmpRegistration"
                target="_blank"
                rel="noreferrer"
                className="flex justify-center max-sm:text-sm items-center gap-2 py-2 md:py-3 px-3 md:px-6 rounded-full bg-s1 border border-mainTextColor text-white font-medium text-md group"
              >
                Sign Up <ArrowUpRight className="group-hover:rotate-[45deg] duration-500 text-base sm:text-xl" />
              </a>
            </div>
          </div>
        </div>
      </header>

      {showLoginModal && <CompanyLoginModal onClose={() => setShowLoginModal(false)} />}

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 left-0 bg-s1/80 h-full w-full lg:hidden duration-700 z-[998] mobileMenuBg ${isMobileMenuOpen ? "mobileMenuBgOpen" : "mobileMenuBgClose"}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <div
        className={`flex justify-start flex-col items-start gap-8 pb-10 fixed lg:hidden top-0 left-0 w-3/4 min-[500px]:w-1/2 h-full bg-s2 overflow-y-auto duration-700 z-[999] mobileMenu ${isMobileMenuOpen ? "mobileMenuOpen" : "mobileMenuClose"}`}
      >
        <div className="flex justify-between items-center w-full px-6 pt-6">
          <img src="assets/img/website/newDesCubLogo.png" alt="CUB HRM" className="h-[48px]" />
          <button className="text-3xl cursor-pointer mobileMenuCloseButton" onClick={() => setIsMobileMenuOpen(false)}>
            ✕
          </button>
        </div>
        <ul className="text-lg sm:text-xl flex gap-6 items-start flex-col pl-8 w-full pr-6" onClick={() => setIsMobileMenuOpen(false)}>
          <li><a href="/" className="menu-hover hover:after:border-p1">Home</a></li>
          <li><a href="/#about" className="menu-hover hover:after:border-p1">About</a></li>

          <li className="w-full" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-mega-toggle" onClick={() => setIsMobileSolutionsOpen((v) => !v)}>
              Solutions <ChevronDown className={`mobile-mega-chevron ${isMobileSolutionsOpen ? "open" : ""}`} />
            </button>
            {isMobileSolutionsOpen && (
              <div className="mobile-mega-list">
                {navSolutions.map((s, i) => (
                  <div key={i} className="mobile-mega-item">
                    <i className={`ph-fill ${s.icon} mobile-mega-icon`} />
                    <span className="mobile-mega-label">{s.title}</span>
                  </div>
                ))}
              </div>
            )}
          </li>

          <li className="w-full" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-mega-toggle" onClick={() => setIsMobileWhyCubOpen((v) => !v)}>
              Why CUB <ChevronDown className={`mobile-mega-chevron ${isMobileWhyCubOpen ? "open" : ""}`} />
            </button>
            {isMobileWhyCubOpen && (
              <div className="mobile-mega-list">
                {whyCubPoints.map((w, i) => (
                  <div key={i} className="mobile-mega-item whycub-mobile-item">
                    <i className={`ph-fill ${w.icon} mobile-mega-icon whycub-mobile-icon`} />
                    <span className="mobile-mega-label">{w.title}</span>
                  </div>
                ))}
              </div>
            )}
          </li>

          <li><a href="/pricing" className="menu-hover hover:after:border-p1" style={{ color: "#1595cc" }}>Pricing</a></li>
          <li><a href="#pp-footer" className="menu-hover hover:after:border-p1">Contact Us</a></li>
          <li>
            <a href="/anonymouseCmpRegistration" target="_blank" rel="noreferrer" className="hover:header_menu_shadow duration-700 px-2 py-3 rounded-lg menu">
              Sign Up
            </a>
          </li>
        </ul>
      </div>

      {/* ── Hero ── */}
      <section className="pricing-page-hero">
        <span className="section-eyebrow">Pricing</span>
        <h1 className="pricing-page-title">Find the right plan for your team</h1>
        <p className="pricing-page-subtitle">
          No hidden fees. No upfront commitment. Talk to our team and get started in days — not months.
        </p>
      </section>

      {/* ── Cards ── */}
      <section className="pricing-page-cards-section">
        <div className="pricing-page-grid">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`pricing-page-card pricing-page-card--white${plan.featured ? " pricing-page-card--featured" : ""}${plan.enterprise ? " pricing-page-card--enterprise" : ""}`}
            >
              {plan.featured && (
                <div className="pricing-popular-badge">Most Popular</div>
              )}

              <div className="pricing-page-card-top">
                <p className={`pp-tier-tag${plan.enterprise ? " pp-tier-tag--enterprise" : ""}`}>{plan.tag}</p>
                <h2 className="pp-tier-name">{plan.name}</h2>
                <p className="pp-tier-desc">{plan.desc}</p>
              </div>

              <ul className="pp-features">
                {plan.features.map((f, j) => (
                  <li key={j} className="pp-feature-item">
                    <i className={`ph-fill ph-check-circle pp-check${plan.enterprise ? " pp-check--enterprise" : ""}`} />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                className={`pp-cta-btn${plan.featured ? " pp-cta-btn--featured" : " pp-cta-btn--enterprise-cta"}`}
                onClick={() => handleCTA(plan.cta)}
              >
                {plan.cta}
                <ArrowUpRight className="pp-cta-icon" />
              </button>
            </div>
          ))}
        </div>

        {showDemoModal && <BookDemoModal onClose={() => setShowDemoModal(false)} />}

        <p className="pricing-footnote">
          All plans include a <strong>14-day free trial</strong>. No credit card required.
        </p>
      </section>

      {/* ── Footer ── */}
      <Footer id="pp-footer" />

      <button
        className="fixed text-white text-xl sm:text-2xl rounded-full bg-p1 hover:bg-s2 border border-p1 hover:border-white right-6 md:right-10 p-2 sm:p-3 z-40 jumping1 duration-700 scrollButton scrollButtonHide !leading-[0]"
        aria-label="scroll to top"
        onClick={handleScrollTop}
      >
        <span className="block"><ArrowUp /></span>
      </button>

    </div>
  );
};

export default PricingPage;
